import "./i18n/i18n";

import type { IPublicClientApplication } from "@azure/msal-browser";
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import { ThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { RouterProvider } from "react-router";

import { loginRequest } from "./auth/authConfig";
import { SnackbarProvider } from "./hooks/useSnackbar";
import { router } from "./routes";
import { shouldRetryQuery } from "./services/queryRetry";
import { mboTheme } from "./styles/theme";

export const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === "true";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: shouldRetryQuery,
      refetchOnWindowFocus: true,
    },
  },
});

export function AutoLoginRedirect({
  msalInstance,
}: {
  msalInstance: IPublicClientApplication;
}) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    msalInstance.loginRedirect(loginRequest).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : String(err));
    });
  }, [msalInstance]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 48, color: "error.main" }} />
        <Typography variant="h6">
          Inloggningen kunde inte startas automatiskt.
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setError(null);
            msalInstance.loginRedirect(loginRequest).catch((err: unknown) => {
              setError(err instanceof Error ? err.message : String(err));
            });
          }}
        >
          Försök igen
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body1">Omdirigerar till inloggning...</Typography>
    </Box>
  );
}

function AuthenticatedContent() {
  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider>
        <Suspense
          fallback={
            <Grid
              container
              sx={{
                height: "100vh",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress aria-label="Laddar..." />
            </Grid>
          }
        >
          <RouterProvider router={router} />
        </Suspense>
      </SnackbarProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

function ErrorFallback() {
  return (
    <Grid
      container
      sx={{
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <ErrorOutlineIcon sx={{ fontSize: 64, mb: 2, color: "error.main" }} />
        <Typography variant="h5" gutterBottom>
          Något gick fel
        </Typography>
        <Button variant="contained" onClick={() => location.reload()}>
          Ladda om
        </Button>
      </Box>
    </Grid>
  );
}

interface AppProps {
  msalInstance?: IPublicClientApplication;
}

export function App({ msalInstance }: AppProps) {
  if (AUTH_BYPASS) {
    return (
      <ThemeProvider theme={mboTheme}>
        <ErrorBoundary fallback={<ErrorFallback />}>
          <AuthenticatedContent />
        </ErrorBoundary>
      </ThemeProvider>
    );
  }

  if (!msalInstance) return null;

  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={mboTheme}>
        <ErrorBoundary fallback={<ErrorFallback />}>
          <AuthenticatedTemplate>
            <AuthenticatedContent />
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <AutoLoginRedirect msalInstance={msalInstance} />
          </UnauthenticatedTemplate>
        </ErrorBoundary>
      </ThemeProvider>
    </MsalProvider>
  );
}
